import { itemDataFromDynamo } from "../contexts/UserDataContext";

export interface PendingItem {
  localId: string;       // = payload.ItemId for saves, "delete_<itemId>" for deletes
  payload: itemDataFromDynamo;
  thumbnailDataUrl: string;
  userId: string;
  createdAt: number;
  attempts: number;
  method?: "DELETE";     // absent = POST/save; "DELETE" = queued delete
  itemIds?: string[];    // only used when method === "DELETE"
}

const DB_NAME = "WheresOurOfflineQueue";
const STORE_NAME = "pendingItems";
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "localId" });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Upsert by localId — last write wins for same item edited multiple times offline
export async function queuePut(item: PendingItem): Promise<void> {
  const { usage, quota } = await navigator.storage.estimate();
  if (usage !== undefined && quota !== undefined && usage / quota > 0.9) {
    console.warn("Storage is nearly full; queueing item anyway");
  }
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(item);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

// Returns items sorted oldest-first via the createdAt index
export async function queueGetAll(): Promise<PendingItem[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).index("createdAt").getAll();
    request.onsuccess = () => { db.close(); resolve(request.result as PendingItem[]); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

export async function queueDelete(localId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(localId);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function queueCount(): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).count();
    request.onsuccess = () => { db.close(); resolve(request.result); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}
